import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { Bomb, PillBottle } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { UpdateItemsCacheApi } from '@/pages/ItemsContainer/Items/api/Item.api';
import { useDialogStore } from '@/store';

// Toast configuration for consistent styling
const toastConfig = {
  success: {
    duration: 3000,
    style: {
      padding: '16px',
      borderRadius: '8px',
      background: '#10b981',
      color: '#ffffff',
    },
    icon: <PillBottle className="text-white" />,
  },
  error: {
    duration: 4000,
    style: {
      padding: '16px',
      borderRadius: '8px',
      background: '#ef4444',
      color: '#ffffff',
    },
    icon: <Bomb className="text-white" />,
  },
};

export function UseUpdateItemsCache() {
  const { openGlobalLoading, closeGlobalLoading, globalLoading } =
    useDialogStore();
  const isGlobalLoading = globalLoading.isLoading;
  const queryClient = useQueryClient();

  // Mutation to update items cache
  const updateItemsCacheMutation = useMutation({
    mutationFn: UpdateItemsCacheApi,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['Items-List'] }),
        queryClient.invalidateQueries({ queryKey: ['group-free-items'] }),
        queryClient.invalidateQueries({ queryKey: ['allItems'] }),
      ]);

      toast.success(
        'تم تحديث الأصناف من البرنامج الرئيسي',
        toastConfig.success,
      );
      closeGlobalLoading();
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      toast.error(err?.response?.data?.message || 'خطأ', toastConfig.error);
      closeGlobalLoading();
    },
  });

  // Use a ref to avoid recreating the handler on every render
  const handlerRef =
    useRef<(event: KeyboardEvent) => Promise<void> | null>(null);

  // Memoize the handler creation using useCallback
  handlerRef.current = useCallback(
    async (event: KeyboardEvent) => {
      if (event.key === 'F2' && event.shiftKey && !isGlobalLoading) {
        openGlobalLoading();
        await updateItemsCacheMutation.mutateAsync();
      }
    },
    [openGlobalLoading, isGlobalLoading, updateItemsCacheMutation],
  );

  useEffect(() => {
    // Use a stable reference to the handler
    const handleKeyPress = (event: KeyboardEvent) => {
      handlerRef.current?.(event);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []); // Empty dependency array since we're using refs

  return { updateItemsCacheMutation };
}
